(function () {
  const instances = new WeakMap();

  class ProductListing {
    constructor(section) {
      this.section = section;
      this.cleanupFunctions = [];
      this.initFilterDrawer();
      this.initInfiniteScroll();
      this.initPriceFilters();
    }

    addCleanup(callback) {
      this.cleanupFunctions.push(callback);
    }

    initFilterDrawer() {
      const openButton = this.section.querySelector('[data-filter-toggle]');
      const drawer = this.section.querySelector('[data-filter-panel]');
      const panel = drawer?.querySelector('.collection-filter-drawer__panel');
      const closeButtons = drawer?.querySelectorAll('[data-filter-close]');

      if (!openButton || !drawer || !panel) return;

      let previouslyFocusedElement = null;

      const getFocusableElements = () =>
        Array.from(
          panel.querySelectorAll(
            'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), details summary, [tabindex]:not([tabindex="-1"])'
          )
        );

      const openDrawer = () => {
        previouslyFocusedElement = document.activeElement;
        drawer.classList.add('is-open');
        drawer.setAttribute('aria-hidden', 'false');
        openButton.setAttribute('aria-expanded', 'true');
        document.documentElement.classList.add('collection-filter-open');

        window.requestAnimationFrame(function () {
          getFocusableElements()[0]?.focus();
        });
      };

      const closeDrawer = () => {
        drawer.classList.remove('is-open');
        drawer.setAttribute('aria-hidden', 'true');
        openButton.setAttribute('aria-expanded', 'false');
        document.documentElement.classList.remove('collection-filter-open');
        previouslyFocusedElement?.focus();
      };

      const handleKeydown = (event) => {
        if (event.key === 'Escape' && drawer.classList.contains('is-open')) {
          closeDrawer();
        }

        if (event.key !== 'Tab' || !drawer.classList.contains('is-open')) return;

        const focusableElements = getFocusableElements();
        if (!focusableElements.length) return;

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (event.shiftKey && document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        } else if (!event.shiftKey && document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      };

      openButton.addEventListener('click', openDrawer);
      closeButtons.forEach(function (button) {
        button.addEventListener('click', closeDrawer);
      });
      document.addEventListener('keydown', handleKeydown);

      this.addCleanup(() => {
        openButton.removeEventListener('click', openDrawer);
        closeButtons.forEach(function (button) {
          button.removeEventListener('click', closeDrawer);
        });
        document.removeEventListener('keydown', handleKeydown);
        document.documentElement.classList.remove('collection-filter-open');
      });
    }

    initInfiniteScroll() {
      const grid = this.section.querySelector('.main-collection__grid');
      const pagination = this.section.querySelector('.main-collection__pagination');
      const infinite = this.section.querySelector('[data-infinite-scroll]');
      const nextLink = this.section.querySelector('[data-infinite-next]');
      const loading = this.section.querySelector('[data-infinite-loading]');

      if (!grid || !infinite || !nextLink) return;

      pagination?.classList.add('is-hidden');

      let nextUrl = nextLink.href;
      let isLoading = false;

      const loadMore = async () => {
        if (isLoading || !nextUrl) return;

        isLoading = true;
        infinite.classList.add('is-loading');
        if (loading) loading.hidden = false;

        try {
          const response = await fetch(nextUrl);
          const text = await response.text();
          const html = new DOMParser().parseFromString(text, 'text/html');

          const newProducts = html.querySelectorAll('.main-collection__grid .product-card');
          const newNextLink = html.querySelector('[data-infinite-next]');

          newProducts.forEach((product) => {
            grid.appendChild(product);
          });

          if (newNextLink) {
            nextUrl = newNextLink.href;
            nextLink.href = nextUrl;
          } else {
            nextUrl = null;
            infinite.remove();
          }
        } catch (error) {
          infinite.classList.add('is-error');
        } finally {
          isLoading = false;
          infinite.classList.remove('is-loading');
          if (loading) loading.hidden = true;
        }
      };

      const observer = new IntersectionObserver(
        function (entries) {
          if (entries[0].isIntersecting) {
            loadMore();
          }
        },
        { rootMargin: '600px 0px' }
      );

      observer.observe(infinite);
      this.addCleanup(() => observer.disconnect());
    }

    initPriceFilters() {
      const moneyFormatter = new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: this.section.dataset.currency || 'GBP',
        maximumFractionDigits: 0
      });

      this.section.querySelectorAll('[data-price-filter]').forEach((filter) => {
        const slider = filter.querySelector('[data-price-slider]');
        const minHandle = filter.querySelector('[data-price-min-handle]');
        const maxHandle = filter.querySelector('[data-price-max-handle]');
        const minInput = filter.querySelector('[data-price-min-input]');
        const maxInput = filter.querySelector('[data-price-max-input]');
        const minDisplay = filter.querySelector('[data-price-min-display]');
        const maxDisplay = filter.querySelector('[data-price-max-display]');

        if (!slider || !minHandle || !maxHandle || !minInput || !maxInput) return;

        const maxLimit = Number(filter.dataset.priceMax) || 0;
        let minValue = Number(minInput.value) || 0;
        let maxValue = Number(maxInput.value) || maxLimit;
        let activeHandle = null;
        let hasDragged = false;

        function clamp(value) {
          return Math.max(0, Math.min(Number(value) || 0, maxLimit));
        }

        function valueFromEvent(event) {
          const rect = slider.getBoundingClientRect();
          const clientX = event.touches ? event.touches[0].clientX : event.clientX;
          const x = clientX - rect.left;
          const percent = Math.max(0, Math.min(x / rect.width, 1));

          return Math.round(percent * maxLimit);
        }

        function update() {
          minValue = clamp(minValue);
          maxValue = clamp(maxValue);

          if (minValue > maxValue) {
            if (activeHandle === minHandle) {
              minValue = maxValue;
            } else {
              maxValue = minValue;
            }
          }

          const minPercent = maxLimit ? (minValue / maxLimit) * 100 : 0;
          const maxPercent = maxLimit ? (maxValue / maxLimit) * 100 : 100;

          slider.style.setProperty('--price-min-percent', `${minPercent}%`);
          slider.style.setProperty('--price-max-percent', `${maxPercent}%`);

          minInput.value = minValue;
          maxInput.value = maxValue;

          if (minDisplay) minDisplay.textContent = moneyFormatter.format(minValue);
          if (maxDisplay) maxDisplay.textContent = moneyFormatter.format(maxValue);
        }

        function moveHandle(event) {
          if (!activeHandle) return;

          hasDragged = true;

          const value = valueFromEvent(event);

          if (activeHandle === minHandle) {
            minValue = value;
          } else {
            maxValue = value;
          }

          update();
        }

        function stopDrag() {
          activeHandle = null;

          document.removeEventListener('pointermove', moveHandle);
          document.removeEventListener('pointerup', stopDrag);
          document.removeEventListener('pointercancel', stopDrag);
        }

        function startDrag(handle, event) {
          event.preventDefault();
          event.stopPropagation();

          activeHandle = handle;
          hasDragged = false;

          moveHandle(event);

          document.addEventListener('pointermove', moveHandle);
          document.addEventListener('pointerup', stopDrag);
          document.addEventListener('pointercancel', stopDrag);
        }

        const startMinDrag = (event) => startDrag(minHandle, event);
        const startMaxDrag = (event) => startDrag(maxHandle, event);

        minHandle.addEventListener('pointerdown', startMinDrag);
        maxHandle.addEventListener('pointerdown', startMaxDrag);

        const handleSliderClick = (event) => {
          if (hasDragged) return;

          const value = valueFromEvent(event);
          const minDistance = Math.abs(value - minValue);
          const maxDistance = Math.abs(value - maxValue);

          activeHandle = minDistance <= maxDistance ? minHandle : maxHandle;

          if (activeHandle === minHandle) {
            minValue = value;
          } else {
            maxValue = value;
          }

          update();
          activeHandle = null;
        };

        slider.addEventListener('click', handleSliderClick);
        update();

        this.addCleanup(() => {
          minHandle.removeEventListener('pointerdown', startMinDrag);
          maxHandle.removeEventListener('pointerdown', startMaxDrag);
          slider.removeEventListener('click', handleSliderClick);
          document.removeEventListener('pointermove', moveHandle);
          document.removeEventListener('pointerup', stopDrag);
          document.removeEventListener('pointercancel', stopDrag);
        });
      });
    }

    destroy() {
      this.cleanupFunctions.forEach((callback) => callback());
      this.cleanupFunctions = [];
    }
  }

  function initialise(section) {
    if (!section || instances.has(section)) return;
    instances.set(section, new ProductListing(section));
  }

  function initialiseAll(root = document) {
    if (root.matches?.('[data-product-listing]')) initialise(root);
    root.querySelectorAll?.('[data-product-listing]').forEach(initialise);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialiseAll());
  } else {
    initialiseAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initialiseAll(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.matches?.('[data-product-listing]')
      ? event.target
      : event.target.querySelector?.('[data-product-listing]');

    if (!section) return;
    instances.get(section)?.destroy();
    instances.delete(section);
  });
})();
